// Custom hook to handle player initialization without circular dependencies

import { useEffect, useState, useCallback, useRef } from 'react';
import { useRoute } from './useRoute';
import { useAuth } from './useAuth';
import { initializePlayer } from '../transport/api/publicApi';
import {
  PlayerDocument,
  SenderRoleType,
  RoomPlayer,
} from '@airhead-games/shared/types';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('usePlayerInitialization');

type InitializationStatus =
  | { state: 'uninitialized' }
  | { state: 'initializing' }
  | { state: 'success' }
  | { state: 'retrying'; attempt: number }
  | { state: 'failed'; error: string; canRetry: boolean };

interface UsePlayerInitializationParams {
  shouldInitializePlayer?: boolean;
  onPlayerLoaded: (player: RoomPlayer | null) => void;
  sessionId: string;
  roomPlayers: RoomPlayer[];
  twitchPlayersOnly: boolean;
}

export const usePlayerInitialization = ({
  shouldInitializePlayer = true,
  onPlayerLoaded,
  sessionId,
  roomPlayers,
  twitchPlayersOnly,
}: UsePlayerInitializationParams) => {
  const { roomCode } = useRoute();
  const { user, isAuthInitialized } = useAuth();
  const [status, setStatus] = useState<InitializationStatus>({
    state: 'uninitialized',
  });

  // Ref to prevent duplicate initialization calls
  const isInitializingRef = useRef(false);

  // Get Twitch ID from authenticated user if available
  const myTwitchId = user?.authTwitch?.userId || '';

  // Reset initialization state whenever room code or twitchPlayersOnly changes
  useEffect(() => {
    setStatus({ state: 'uninitialized' });
  }, [roomCode, twitchPlayersOnly]);

  const mapPlayerFromDocument = useCallback(
    (player: PlayerDocument): RoomPlayer => ({
      playerId: player._id,
      playerName: player.playerName,
      playerColor: player.playerColor,
      face: player.face,
      // Room-specific fields will be initialized by room state management
      role: SenderRoleType.PLAYER,
      isEliminated: false,
      scoreTurn: 0,
      scoreGame: 0,
      teamId: undefined,
      hasTwitchAuth: !!player.authTwitch?.userId,
      twitchUserId: player.authTwitch?.userId || undefined,
    }),
    []
  );

  const loadPlayerFromDbOrRoom = useCallback(
    (dbPlayer: PlayerDocument): RoomPlayer => {
      const playerId = dbPlayer._id;

      // Check if this player already exists in room state
      const existingRoomPlayer = roomPlayers.find(
        (p) => p.playerId === playerId
      );

      if (existingRoomPlayer) {
        logger.debug('Player found in room state, using existing room data');
        return existingRoomPlayer;
      } else {
        logger.debug('Player not in room, using DB data with defaults');
        return mapPlayerFromDocument(dbPlayer);
      }
    },
    [roomPlayers, mapPlayerFromDocument]
  );

  const doInitializePlayer = useCallback(async () => {
    // Get current retry count before changing state
    const currentAttempt = status.state === 'retrying' ? status.attempt : 0;

    try {
      isInitializingRef.current = true;
      setStatus({ state: 'initializing' });

      // Prepare initialization params
      const params: {
        sessionId: string;
        twitchId?: string;
        twitchPlayersOnly: boolean;
      } = { sessionId, twitchPlayersOnly };

      if (twitchPlayersOnly && myTwitchId) {
        params.twitchId = myTwitchId;
      }

      logger.debug('Initializing player with params:', params);
      const response = await initializePlayer(params);

      if (response.success && response.data) {
        const playerDocument = response.data.player;
        const playerToLoad = loadPlayerFromDbOrRoom(playerDocument);
        onPlayerLoaded(playerToLoad);
        isInitializingRef.current = false;
        setStatus({ state: 'success' });
        logger.debug('Player initialization completed successfully');

        // Clean up Twitch auth completion URL parameter if present
        if (typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('twitchAuthComplete') === 'true') {
            urlParams.delete('twitchAuthComplete');
            const newUrl = `${window.location.pathname}${
              urlParams.toString() ? `?${urlParams.toString()}` : ''
            }`;
            window.history.replaceState({}, '', newUrl);
          }
        }

        return;
      } else {
        throw new Error('Player initialization returned unsuccessful response');
      }
    } catch (error) {
      isInitializingRef.current = false;
      logger.error('Failed to initialize player', error);

      // Retry up to 3 times with exponential backoff
      if (currentAttempt < 3) {
        const nextAttempt = currentAttempt + 1;
        const retryDelay = Math.pow(2, currentAttempt) * 2000; // 2s, 4s, 8s
        logger.info(
          `Retrying player initialization in ${retryDelay}ms (attempt ${nextAttempt}/3)`
        );
        setStatus({ state: 'retrying', attempt: nextAttempt });
        setTimeout(() => {
          setStatus({ state: 'uninitialized' }); // This will trigger the useEffect to retry
        }, retryDelay);
      } else {
        // After 3 failed attempts, show error but don't block UI
        setStatus({
          state: 'failed',
          error: 'Failed to load player data.',
          canRetry: false,
        });
        logger.warn('Player initialization failed after 3 attempts');
      }
    }
  }, [
    myTwitchId,
    twitchPlayersOnly,
    sessionId,
    onPlayerLoaded,
    loadPlayerFromDbOrRoom,
    status,
  ]);

  useEffect(() => {
    if (
      // Don't run if player initialization is not needed for this route
      !shouldInitializePlayer ||
      // Wait for auth to initialize before proceeding (to get user.authTwitch if available)
      !isAuthInitialized ||
      // Don't re-initialize if we've already succeeded or are currently initializing
      status.state === 'success' ||
      status.state === 'initializing' ||
      status.state === 'retrying' ||
      // Synchronous ref check - prevents duplicate calls in same render cycle
      isInitializingRef.current
    ) {
      return;
    }

    doInitializePlayer(); // Proceed with initialization
  }, [
    shouldInitializePlayer,
    isAuthInitialized,
    status.state,
    doInitializePlayer,
  ]);

  // Treat "will initialize but the effect hasn't fired yet" as initializing,
  // so the overlay stays open through the render gap between roomState
  // arriving and the init effect setting status to 'initializing'.
  const isPlayerInitializing =
    shouldInitializePlayer &&
    status.state !== 'success' &&
    status.state !== 'failed';
  const playerInitializationError =
    status.state === 'failed' ? status.error : null;
  const playerInitializationFailed = status.state === 'failed';

  return {
    isPlayerInitializing,
    playerInitializationError,
    playerInitializationFailed,
    reinitializePlayer: doInitializePlayer,
  };
};
